import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, BookOpen, Home, ChevronRight, Bookmark, ArrowUp, BookmarkX } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useApp, type DocumentBookmark } from './AppContext';
import type { Screen } from '../App';

// Загрузчики документов через dynamic import — вынесены в отдельные code-split чанки
const DOCUMENT_LOADERS: Record<string, () => Promise<string>> = {
    'doc-77fz': () => import('../data/documents/Federal_Law_No._77_FZ_of_14_April_1999_on_Departmental_Security.md?raw').then(m => m.default),
    'doc-weapons': () => import('../data/documents/FEDERAL_LAW_ON_WEAPONS.md?raw').then(m => m.default),
    'doc-periodic-check': () => import('../data/documents/The_order_of_the_Russian_Guard.md?raw').then(m => m.default),
    'doc-criminal-code': () => import('../data/documents/Articles_of_the_Criminal_Code_of_the_Russian_Federation.md?raw').then(m => m.default),
};

// Document metadata for SEO (without content — loads lazily)
interface DocumentMeta {
    id: string;
    title: string;
    seoTitle: string;
    seoDescription: string;
    schemaType: 'LegalDocument' | 'GovernmentOrganization';
    datePublished?: string;
    dateModified?: string;
}

const DOCUMENT_META: Record<string, DocumentMeta> = {
    'doc-77fz': {
        id: '77fz',
        title: 'ФЗ-77 «О ведомственной охране»',
        seoTitle: 'ФЗ-77 «О ведомственной охране» — Полный текст | ВОХР-Тест',
        seoDescription: 'Федеральный закон №77-ФЗ от 14.04.1999 «О ведомственной охране». Права и обязанности охранника, применение оружия и спецсредств.',
        schemaType: 'LegalDocument',
        datePublished: '1999-04-14',
        dateModified: '2025-07-31'
    },
    'doc-weapons': {
        id: 'weapons',
        title: 'ФЗ «Об оружии» №150-ФЗ',
        seoTitle: 'Билеты ведомсвенной охраны с оружием — ФЗ «Об оружии» | ВОХР-Тест',
        seoDescription: 'Федеральный закон «Об оружии» №150-ФЗ для подготовки ВОХР: хранение, ношение и применение служебного оружия.',
        schemaType: 'LegalDocument',
        datePublished: '1996-12-13'
    },
    'doc-periodic-check': {
        id: 'periodic-check',
        title: 'Приказ Росгвардии №178 — Периодическая проверка',
        seoTitle: 'Периодическая проверка ведомственной охраны онлайн — Приказ №178 | ВОХР-Тест',
        seoDescription: 'Порядок периодической проверки ведомственной охраны: пригодность к применению оружия, упражнения по стрельбе, спецсредсва и онлайн-подготовка к тесту ВОХР.',
        schemaType: 'LegalDocument',
        datePublished: '2026-05-19'
    },
    'doc-criminal-code': {
        id: 'criminal-code',
        title: 'Статьи УК РФ для охранников',
        seoTitle: 'Статьи УК РФ для охранников ВОХР — Необходимая оборона | ВОХР-Тест',
        seoDescription: 'Уголовная ответственность охранника: необходимая оборона, крайняя необходимость, превышение полномочий.',
        schemaType: 'LegalDocument'
    }
};

interface DocumentDetailPageProps {
    documentType: 'doc-77fz' | 'doc-weapons' | 'doc-periodic-check' | 'doc-criminal-code';
    onNavigate: (screen: Screen) => void;
    onBack: () => void;
}

export const DocumentDetailPage: React.FC<DocumentDetailPageProps> = ({ documentType, onNavigate, onBack }) => {
    const {
        getBackgroundClass,
        settings,
        updateReadingProgress,
        getReadingProgress,
        getDocumentInternalBookmarks,
        addDocumentInternalBookmark,
        removeDocumentInternalBookmark,
        selectedBookmark,
        setSelectedBookmark
    } = useApp();

    const meta = DOCUMENT_META[documentType];

    // State
    const [currentPosition, setCurrentPosition] = useState(0);
    const [showBookmarks, setShowBookmarks] = useState(false);
    const [showAddBookmark, setShowAddBookmark] = useState(false);
    const [bookmarkTitle, setBookmarkTitle] = useState('');
    const [contextMenuPosition, setContextMenuPosition] = useState<number | null>(null);
    const [contextMenuText, setContextMenuText] = useState('');
    const [isProgressRemoved, setIsProgressRemoved] = useState(false);
    // Содержимое документа — загружается лениво через dynamic import
    const [docContent, setDocContent] = useState<string>('');
    const [isDocLoading, setIsDocLoading] = useState(true);

    // Загружаем текст документа при смене типа документа
    useEffect(() => {
        setIsDocLoading(true);
        DOCUMENT_LOADERS[documentType]()
            .then(c => { setDocContent(c); setIsDocLoading(false); })
            .catch(() => setIsDocLoading(false));
    }, [documentType]);

    // Refs
    const contentRef = useRef<HTMLDivElement>(null);
    const lineRefs = useRef<(HTMLDivElement | null)[]>([]);
    const currentScrollPosition = useRef(0);
    const longPressTimer = useRef<NodeJS.Timeout | null>(null);

    // Calculate font size
    const baseFontSize = 16;
    const fontSize = Math.round((settings.documentFontSize / 50) * baseFontSize);

    // Handle back with progress save
    const handleBack = () => {
        if (meta && currentScrollPosition.current > 0 && !isProgressRemoved) {
            updateReadingProgress(meta.id, currentScrollPosition.current);
        }
        onBack();
    };

    // Save on unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (meta && currentScrollPosition.current > 0 && !isProgressRemoved) {
                updateReadingProgress(meta.id, currentScrollPosition.current);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handleBeforeUnload);
        };
    }, [meta, updateReadingProgress, isProgressRemoved]);

    // Restore progress on mount
    useEffect(() => {
        if (!meta) return;

        const progress = getReadingProgress(meta.id);
        setIsProgressRemoved(false);

        if (progress && progress.position > 0) {
            setCurrentPosition(progress.position);
            currentScrollPosition.current = progress.position;

            setTimeout(() => {
                const targetElement = lineRefs.current[progress.position];
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
                }
            }, 100);
        } else if (selectedBookmark && selectedBookmark.documentId === meta.id) {
            setTimeout(() => {
                const targetElement = lineRefs.current[selectedBookmark.position];
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setSelectedBookmark(null);
                }
            }, 100);
        }
    }, [meta, getReadingProgress, selectedBookmark, setSelectedBookmark]);

    // Track scroll position
    useEffect(() => {
        if (!contentRef.current) return;

        const scrollContainer = contentRef.current;

        const handleScroll = () => {
            const containerRect = scrollContainer.getBoundingClientRect();
            const centerY = containerRect.top + containerRect.height / 2;

            let closestLine = 0;
            let closestDistance = Infinity;

            lineRefs.current.forEach((lineRef, index) => {
                if (lineRef) {
                    const rect = lineRef.getBoundingClientRect();
                    const distance = Math.abs(rect.top + rect.height / 2 - centerY);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestLine = index;
                    }
                }
            });

            setCurrentPosition(closestLine);
            currentScrollPosition.current = closestLine;
        };

        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    // Long press handlers
    const handleTouchStart = (position: number, text: string) => {
        longPressTimer.current = setTimeout(() => {
            setContextMenuPosition(position);
            setContextMenuText(text.substring(0, 100));
            setBookmarkTitle(text.substring(0, 50));
            setShowAddBookmark(true);
        }, 500);
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    // Add bookmark
    const confirmAddBookmark = () => {
        if (contextMenuPosition !== null && bookmarkTitle.trim() && meta) {
            const bookmark: DocumentBookmark = {
                id: `${meta.id}-${Date.now()}`,
                documentId: meta.id,
                position: contextMenuPosition,
                text: contextMenuText,
                title: bookmarkTitle.trim(),
                timestamp: Date.now()
            };
            addDocumentInternalBookmark(bookmark);
            setShowAddBookmark(false);
            setBookmarkTitle('');
            setContextMenuPosition(null);
        }
    };

    // Jump to bookmark
    const handleJumpToBookmark = (bookmark: DocumentBookmark) => {
        const targetElement = lineRefs.current[bookmark.position];
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setShowBookmarks(false);
        }
    };

    // Scroll to top
    const handleScrollToTop = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Remove progress
    const handleRemoveProgress = () => {
        if (meta) {
            setIsProgressRemoved(true);
            updateReadingProgress(meta.id, 0);
            handleScrollToTop();
        }
    };

    // Update SEO meta tags
    useEffect(() => {
        if (!meta) return;

        const schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.id = 'document-detail-schema';
        schemaScript.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": meta.schemaType,
            "name": meta.title,
            "description": meta.seoDescription,
            "datePublished": meta.datePublished,
            "dateModified": meta.dateModified,
            "legislationLegalForce": "InForce",
            "jurisdiction": "Russia"
        });

        const existingSchema = document.getElementById('document-detail-schema');
        if (existingSchema) existingSchema.remove();
        document.head.appendChild(schemaScript);

        return () => {
            const schema = document.getElementById('document-detail-schema');
            if (schema) schema.remove();
        };
    }, [meta]);

    if (!meta) {
        return <div className="glass-text p-4">Документ не найден</div>;
    }

    if (isDocLoading) {
        return (
            <div className="screen-min-h-full flex items-center justify-center">
                <div className="glass-text-secondary text-sm">Загрузка документа…</div>
            </div>
        );
    }

    const contentLines = docContent.split('\n');
    const bookmarks = getDocumentInternalBookmarks(meta.id);
    const progress = getReadingProgress(meta.id);

    // Get positions of bookmarked lines
    const bookmarkedPositions = new Set(bookmarks.map(b => b.position));

    // Render markdown line
    const renderLine = (line: string, index: number) => {
        const baseStyle = { fontSize: `${fontSize}px`, lineHeight: '1.65' };
        const setRef = (el: HTMLDivElement | null) => { lineRefs.current[index] = el; };
        const touchHandlers = {
            onTouchStart: () => handleTouchStart(index, line),
            onTouchEnd: handleTouchEnd,
            onMouseDown: () => handleTouchStart(index, line),
            onMouseUp: handleTouchEnd,
            onMouseLeave: handleTouchEnd
        };

        // Check if this line is bookmarked
        const isBookmarked = bookmarkedPositions.has(index);
        const bookmarkStyle = isBookmarked ? {
            borderLeft: '3px solid #fb2c36',
            paddingLeft: '8px',
            marginLeft: '-11px'
        } : {};

        if (line.startsWith('# ')) {
            return <h1 key={index} ref={setRef} {...touchHandlers} className="font-bold glass-text mb-3 mt-4" style={{ fontSize: `${fontSize * 1.5}px`, ...bookmarkStyle }}>{line.substring(2)}</h1>;
        }
        if (line.startsWith('## ')) {
            return <h2 key={index} ref={setRef} {...touchHandlers} className="font-semibold glass-text mb-2 mt-4" style={{ fontSize: `${fontSize * 1.25}px`, ...bookmarkStyle }}>{line.substring(3)}</h2>;
        }
        if (line.startsWith('### ')) {
            return <h3 key={index} ref={setRef} {...touchHandlers} className="font-medium glass-text mb-2 mt-3" style={{ fontSize: `${fontSize * 1.1}px`, ...bookmarkStyle }}>{line.substring(4)}</h3>;
        }
        if (line.startsWith('- ')) {
            return (
                <div key={index} ref={setRef} {...touchHandlers} className="flex items-start gap-2 mb-1" style={bookmarkStyle}>
                    <span className="glass-text mt-1">•</span>
                    <span className="glass-text-secondary flex-1" style={baseStyle}>{line.substring(2)}</span>
                </div>
            );
        }
        if (line.match(/^\d+\. /)) {
            const num = line.match(/^\d+\./)?.[0];
            return (
                <div key={index} ref={setRef} {...touchHandlers} className="flex items-start gap-2 mb-1" style={bookmarkStyle}>
                    <span className="glass-text font-medium" style={baseStyle}>{num}</span>
                    <span className="glass-text-secondary flex-1" style={baseStyle}>{line.replace(/^\d+\. /, '')}</span>
                </div>
            );
        }
        if (line.startsWith('**') && line.endsWith('**')) {
            return <p key={index} ref={setRef} {...touchHandlers} className="font-semibold glass-text mb-2" style={{ ...baseStyle, ...bookmarkStyle }}>{line.substring(2, line.length - 2)}</p>;
        }
        if (line.trim() === '') {
            return <div key={index} ref={setRef} className="h-2" />;
        }
        return <p key={index} ref={setRef} {...touchHandlers} className="glass-text-secondary mb-2" style={{ ...baseStyle, ...bookmarkStyle }}>{line}</p>;
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    };

    return (
        <div className={`h-full flex-1 w-full min-h-0 ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col pt-0 sm:pt-4 md:pt-6`}>
            {/* Header */}
            <header className="app-header-fixed flex items-center">
                <button onClick={handleBack} className="mr-2 sm:mr-3 arrow-button flex-shrink-0" aria-label="Назад">
                    <ArrowLeft className="arrow-icon" />
                </button>
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
                    <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold glass-text truncate">{meta.title}</h1>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    {progress && progress.position > 0 && !isProgressRemoved && (
                        <button onClick={handleRemoveProgress} className="arrow-button" aria-label="Сбросить прогресс">
                            <BookmarkX className="arrow-icon" />
                        </button>
                    )}
                    {currentPosition > 50 && (
                        <button onClick={handleScrollToTop} className="arrow-button" aria-label="Наверх">
                            <ArrowUp className="arrow-icon" />
                        </button>
                    )}
                    <button onClick={() => setShowBookmarks(true)} className="arrow-button relative" aria-label="Закладки">
                        <Bookmark className="arrow-icon" />
                        {bookmarks.length > 0 && (
                            <span
                                className="absolute -top-1 -right-1 text-xs text-white font-semibold rounded-full h-5 w-5 flex items-center justify-center glass-danger border"
                                style={{
                                    backgroundColor: 'rgba(251, 113, 133, 0.2)',
                                    borderColor: 'rgba(251, 113, 133, 0.3)',
                                    backdropFilter: 'blur(12px)',
                                    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px'
                                }}
                            >
                                {bookmarks.length}
                            </span>
                        )}
                    </button>
                </div>
            </header>

            {/* Breadcrumbs */}
            <nav className="px-4 py-2 flex items-center gap-2 text-xs sm:text-sm glass-text-secondary" aria-label="Навигация">
                <button onClick={() => onNavigate('main')} className="hover:underline flex items-center gap-1">
                    <Home className="h-3 w-3" /> Главная
                </button>
                <ChevronRight className="h-3 w-3" />
                <button onClick={() => onNavigate('documents-catalog')} className="hover:underline">Документы</button>
                <ChevronRight className="h-3 w-3" />
                <span className="glass-text truncate">{meta.title}</span>
            </nav>

            {/* Content */}
            <main ref={contentRef} className="app-content-scrollable">
                <article className="glass-panel rounded-xl p-4 sm:p-6 md:p-8">
                    {contentLines.map((line, index) => renderLine(line, index))}
                </article>
            </main>

            {/* Add Bookmark Dialog */}
            <Dialog open={showAddBookmark} onOpenChange={setShowAddBookmark}>
                <DialogContent className="max-w-md mx-auto" style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Добавить закладку</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="mb-4">
                            <label className="block text-sm font-medium glass-text mb-2">Название закладки</label>
                            <input
                                type="text"
                                value={bookmarkTitle}
                                onChange={(e) => setBookmarkTitle(e.target.value)}
                                className="w-full px-3 py-2 glass-panel rounded-lg glass-text"
                                placeholder="Введите название..."
                                autoFocus
                            />
                        </div>
                        {contextMenuText && (
                            <p className="text-xs glass-text-muted mb-4 line-clamp-2">{contextMenuText}</p>
                        )}
                        <div className="flex gap-2 justify-end">
                            <Button onClick={() => { setShowAddBookmark(false); setBookmarkTitle(''); }} variant="outline" className="glass-interactive">
                                Отмена
                            </Button>
                            <Button onClick={confirmAddBookmark} disabled={!bookmarkTitle.trim()} className="glass-interactive">
                                Добавить
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bookmarks Dialog */}
            <Dialog open={showBookmarks} onOpenChange={setShowBookmarks}>
                <DialogContent className="max-w-md mx-auto" style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Закладки ({bookmarks.length})</DialogTitle>
                    </DialogHeader>
                    <div className="py-2 max-h-80 overflow-y-auto">
                        {bookmarks.length === 0 ? (
                            <div className="text-center py-8">
                                <BookmarkX className="h-12 w-12 glass-icon opacity-40 mx-auto mb-3" />
                                <p className="glass-text-secondary">Закладок пока нет</p>
                                <p className="text-xs glass-text-muted mt-1">Долгое нажатие на текст для создания</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {bookmarks.map(bookmark => (
                                    <div key={bookmark.id} className="glass-panel rounded-lg p-3 glass-interactive" onClick={() => handleJumpToBookmark(bookmark)}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Bookmark className="h-4 w-4 glass-icon" />
                                                    <span className="text-sm font-medium glass-text">{bookmark.title}</span>
                                                </div>
                                                <p className="text-xs glass-text-secondary line-clamp-2 mb-1">{bookmark.text}</p>
                                                <span className="text-xs glass-text-muted">{formatDate(bookmark.timestamp)}</span>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); removeDocumentInternalBookmark(bookmark.id); }}
                                                className="p-2 glass-interactive rounded-lg text-red-400 hover:text-red-300"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
